<template>
  <div class="flex flex-column flex-grow-1" :class="`${AppConfig.theme}-theme`">
    <label class="label-top"><font-awesome-icon
      icon="fa-light fa-circle-phone-flip"
      class="phone-icon mr-1"
    ></font-awesome-icon
    >{{ props.label }}</label>
    <div class="p-float-label">
      <InputText
        v-model:model-value="v$.value.$model"
        @input="$emit('update:modelValue', telephoneToMsisdn($event.target.value))"
        @blur="$emit('blur')"
        v-bind="$attrs"
        style="width: 100%"
      ></InputText>
      <label class="label-border">{{ props.label }}</label>
    </div>
    <div class="flex">
      <small v-if="v$.value.$error" class="p-error">{{ errorMessage }}</small>
      <small v-if="v$.value.$error == [] && props.required" id="nin_number-help" class="help-text"
        >*{{ t('general.required') }}</small
      >
    </div>
  </div>
</template>
<script setup>
import { computed, ref, watch } from 'vue'
import { useVuelidate } from '@vuelidate/core'
import { useUserStore } from '@/stores/userStore'
import { useI18nUtils } from '@/composables/utils/useI18nUtils.js'
import telephoneCodes from '@/data/telephoneCodes'
import AppConfig from '~/config/appConfig.js'

const props = defineProps({
  modelValue: String,
  label: String,
  required: Boolean
})

const { t } = useI18nUtils()

const msisdnCountryCode = computed(() => {
  const userStore = useUserStore()
  const countryCode = userStore.partner?.country
  return typeof countryCode === 'string' ? countryCode : ''
})

function getTelephoneCode(countryCode) {
  return telephoneCodes[countryCode.value] || ''
}

const msisdnTelephoneCode = computed(() => getTelephoneCode(msisdnCountryCode))

function telephoneToMsisdn(telephone) {
  const digits = telephone.replace(/^0/, '').replaceAll(/\D/g, '')
  // A cleared phone is no phone: the bare calling code would be saved as the farmer's number
  return digits ? msisdnTelephoneCode.value + digits : ''
}

function msisdnToTelephone(msisdn) {
  const countryCode = msisdnTelephoneCode.value
  let telephone = msisdn || ''
  
  // Strip leading country code if present
  if (countryCode && telephone.startsWith(countryCode)) {
    telephone = telephone.slice(countryCode.length)
  }

  // Convert to local format
  if (msisdnCountryCode.value === 'ZA' || msisdnCountryCode.value === 'UG') {
    telephone = telephone.replace(/^/, '0') // ensure leading 0
  }
  
  return telephone
}

const errorMessage = computed(() => {
  switch (msisdnCountryCode.value) {
    case 'ZA':
      return 'Phone number must be 10 digits and start with 0 (e.g. 0123456789).'
    case 'CO':
      return 'Phone number must be 10 digits and start with 3 (e.g. 3XXXXXXXXX).'
    default:
      return 'Invalid phone number format.'
  }
})


// An empty number is valid unless the field is required: the existing and new farmer forms leave the
// farmer's own phone optional, and an empty one used to keep their Submit disabled with no asterisk
const validTelephone = (country_code) => (value) => {
  if (!value || !value.length) return !props.required

  // Remove non-digits
  let telephone = value.replaceAll(/\D/g, '')

  // If starts with country code, strip it
  const code = getTelephoneCode({ value: country_code })
  if (telephone.startsWith(code)) {
    telephone = telephone.slice(code.length)
  }

  // Validate against local patterns
  if (country_code === 'ZA' || country_code === 'UG') {
    return /^0?\d{9}$/.test(telephone)
  }

  if (country_code === 'CO') {
    return /^3\d{9}$/.test(telephone)
  }

  return telephone.length >= 9
}

const value = ref(msisdnToTelephone(props.modelValue))
const rules = computed(() => ({
  value: {
    Telephone: validTelephone(msisdnCountryCode.value)
  }
}))

const v$ = useVuelidate(rules, { value })

watch(
  () => props.modelValue,
  (newVal) => {
    if(!newVal) {
      value.value = ''
      v$.value.$reset()
    }
  },
  { immediate: true }
)

</script>
<style scoped>
label {
  font-weight: 550;
  color: #013315;
}

input {
  height: 40px;
  box-shadow: 1px 2px 4px #00000029;
  border: 0.5px solid #E5E5E5;
  border-radius: 8px;
}
.label-top {
  margin-bottom: 8px;
  display: none;
}

@media screen and (min-width: 768px) {
  .label-top {
    display: block;
  }

  .label-border {
    display: none;
  }
}
.sbg-theme {
  label {
    color: rgba(0, 0, 0, 0.6);
    font-weight: normal;
    width: auto;
  }

  .label-top {
    display: none;
  }

  .label-border {
    display: block;
  }

  input:focus ~ label {
    color: #00afa5;
  }

  input {
    height: auto;
    line-height: normal;
    box-shadow: none;
    border: 1px solid rgba(0, 0, 0, 0.38);
  }

  input:focus {
    box-shadow: inset 0 0 0 1px #00afa5, inset 0 0 0 1px #00afa5, inset 0 0 0 1px #00afa5, inset 0 0 0 1px #00afa5;
  }
}
</style>
